import React, { useState, useContext, useEffect, useRef } from "react";
import { gql, useQuery, useMutation, useApolloClient } from "@apollo/client";
import styled from "styled-components";
import { FaPlus } from "react-icons/fa";
import { TbRefresh } from "react-icons/tb";
import {
  LoadingView,
  NotificationContext,
} from "@solsys/solsys-reactjs";
import { DateTime } from "luxon";
import { Button } from "@solsys/solsys-reactjs/dist/BasicLayout";
import { useAuth } from "../../contexts/AuthContext";
import { useUserList } from "../../contexts/UserListContext";
import { hasAnyRole } from "../../utils/roles";
import LoadImage from "../elements/loadImage";
import MessageWithLinks, { withDisplayMentions } from "../elements/MessageWithLinks";
import ChatSendButton from "../elements/ChatSendButton";
import { useIdempotentChatSubmit, useIdempotentCreate, mutationInput, createIdempotencyKey } from "../../utils/idempotency";
import SaveButton from "../elements/SaveButton";
import { TICKET_EVENT_TYPE, isImageTypeId, isPartTypeId } from "../../eventTypes";


// Styled Components
const EventContainer = styled.div`
  background: var(--chat-thread-bg);
  color: var(--text-color);
  padding: 20px;
  border-radius: 8px;
  max-width: 100%;
  box-shadow: var(--shadow-card);
  font-family: "Arial", sans-serif;
  margin: 10px auto;
  ${({ $phone }) => $phone && `
    display: flex;
    flex-direction: column;
    background: transparent;
    padding: 0;
    border-radius: 0;
    box-shadow: none;
    margin: 0;
    font-family: inherit;
  `}
`;


const PartBubble = styled.div`
  background-color: ${({ $phone }) => ($phone ? "var(--card-bg)" : "var(--chat-part-bg)")};
  color: ${({ $phone }) => ($phone ? "var(--text-color)" : "var(--chat-part-text)")};
  padding: 12px 18px;
  border-radius: ${({ $phone }) => ($phone ? "16px" : "12px")};
  margin: ${({ $phone }) => ($phone ? "8px 0" : "8px 20px")};
  max-width: ${({ $phone }) => ($phone ? "80%" : "70%")};
  align-self: flex-start;
  border: 1px solid ${({ $phone }) => ($phone ? "var(--card-border)" : "var(--chat-part-border)")};
  box-shadow: ${({ $phone }) => ($phone ? "none" : "var(--shadow-card)")};
  font-size: ${({ $phone }) => ($phone ? "16px" : "inherit")};

  .part-title {
    font-weight: 600;
    margin-bottom: 4px;
  }

  .time {
    font-size: 11px;
    font-style: italic;
    margin-top: 6px;
    color: var(--chat-other-meta);
  }
`;

const MessageBubble = styled.div`
  padding: 12px 18px;
  border-radius: ${({ $phone }) => ($phone ? "16px" : "20px")};
  margin: 8px 0;
  max-width: ${({ $phone }) => ($phone ? "80%" : "70%")};
  font-size: ${({ $phone }) => ($phone ? "16px" : "inherit")};
  align-self: ${({ own }) => (own ? "flex-end" : "flex-start")};
  color: ${({ own, $phone }) => ($phone ? (own ? "var(--header-text)" : "var(--text-color)") : (own ? "var(--chat-own-text)" : "var(--chat-other-text)"))};
  background-color: ${({ own, $phone }) => ($phone ? (own ? "var(--header-bg)" : "var(--card-bg)") : (own ? "var(--chat-own-bg)" : "var(--chat-other-bg)"))};
  border: ${({ own, $phone }) => ($phone && !own ? "1px solid var(--card-border)" : "none")};
  box-shadow: ${({ $phone }) => ($phone ? "none" : "var(--shadow-card)")};
  margin-right: ${({ own, $phone }) => ($phone ? "0" : own ? "20px" : "0")};
  margin-left: ${({ own, $phone }) => ($phone ? "0" : own ? "0" : "20px")};


  .changes {
    margin-top: 6px;
    padding: 6px;
    border-radius: 8px;
    font-style: italic;
  }


  .time {
    font-size: 11px;
    font-style: italic;
    margin-top: 4px;
  }
`;


const MessageInput = styled.div`
  background-color: var(--chat-shell-bg);
  padding: 10px;
  border-radius: 20px;
  margin-top: 10px;
  ${({ $phone }) => $phone && `
    background: transparent;
    padding: 8px 0;
    border-radius: 0;
    margin-top: 0;
    display: flex;
    flex-direction: column;
  `}
`;


const Input = styled.input`
  border: none;
  outline: none;
  flex-grow: 1;
  padding: 8px 10px;
  border-radius: 20px;
  background: var(--chat-input-bg);
  color: var(--chat-input-text);
  ${({ $phone }) => $phone && `
    flex: 1 1 0;
    min-width: 0;
    width: auto;
    min-height: 44px;
    font-size: 16px;
    border-radius: 12px;
    border: 1px solid var(--input-border);
  `}
`;


const ControlPanel = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 5px 0;
`;


const ControlLabel = styled.div`
  font-size: 14px;
  color: #333;
  margin-right: 10px;
`;


const entityQuery = gql`
  query ticketEventQuery(
    $ticket_event_list_request: ticket_event_list_request
    $ticket_list_request: ticket_list_request
  ) {
    ticket_event(ticket_event_list_request: $ticket_event_list_request) {
      edges {
        id
        created_at
        type_id
        ticket_event_type {
          id
          name
        }
        message
        changes
        user_display_name
        user_id
        part_id
        service_part {
          id
          part_no
          quantity
          description
        }
      }
    }
    ticket(ticket_list_request: $ticket_list_request) {
      edges {
        id
        name
        description
        status_id
      }
    }
  }
`;


const ticketEventCheckQuery = gql`
  query ticketEventCheckQuery(
    $ticket_event_check_list_request: ticket_event_check_list_request
  ) {
    ticket_event_check(ticket_event_check_list_request: $ticket_event_check_list_request) {
      edges {
        id
        receiver_id
        ticket_id
      }
    }
  }
`;


const subscriptionQuery = gql`
  query ticketEventQuery(
    $ticket_subscription_list_request: ticket_subscription_list_request
    $ticket_notification_list_request: ticket_notification_list_request
  ) {
    ticket_subscription(ticket_subscription_list_request: $ticket_subscription_list_request) {
      edges {
        id
        ticket_id
        user_id
        created_at
      }
    }
    ticket_notification(ticket_notification_list_request: $ticket_notification_list_request) {
      edges {
        id
        ticket_id
        user_id
        sender_id
      }
    }
  }
`;


const entityMutation = gql`
  mutation ticketEventMutate($ticket_event_mutation_request: MutationInput) {
    ticket_event(ticket_event_mutation_request: $ticket_event_mutation_request) {
      id
      isNew
    }
  }
`;


const ticketMutation = gql`
  mutation ticketMutate($ticket_mutation_request: MutationInput) {
    ticket(ticket_mutation_request: $ticket_mutation_request) {
      id
      isNew
    }
  }
`;


const ticketSubscription = gql`
  mutation ticketSubscriptionMutate($ticket_subscription_mutation_request: MutationInput) {
    ticket_subscription(ticket_subscription_mutation_request: $ticket_subscription_mutation_request) {
      id
      isNew
    }
  }
`;


const eventCheckMutation = gql`
  mutation ticketEventCheckMutate($ticket_event_check_mutation_request: MutationInput) {
    ticket_event_check(ticket_event_check_mutation_request: $ticket_event_check_mutation_request) {
      id
      isNew
    }
  }
`;


const servicePartMutation = gql`
  mutation servicePartMutate($service_part_mutation_request: MutationInput) {
    service_part(service_part_mutation_request: $service_part_mutation_request) {
      id
      isNew
    }
  }
`;


const serverUrl = process.env.REACT_APP_API_URL || "RUNTIME_REPLACE_REACT_APP_API_URL";


// Ticket Event Component
const TicketEvent = ({ ticket_id, crane_id, phone = false }) => {
  const { addNotification } = useContext(NotificationContext);
  const { user } = useAuth();
  const canAddParts = hasAnyRole(user, ["administrator", "technician"]);
  const canAssignTechnicians = hasAnyRole(user, ["administrator", "technician"]);
  const [newMessage, setNewMessage] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [subscriptionId, setSubscriptionId] = useState(0);
  const [images, setImages] = useState([]);
  const [previewImages, setPreviewImages] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const [showPartsModal, setShowPartsModal] = useState(false);
  const [partQuantity, setPartQuantity] = useState("");
  const [partNumber, setPartNumber] = useState("");
  const partFingerprint = JSON.stringify({
    ticket_id,
    quantity: partQuantity,
    partNumber,
  });
  const {
    idempotencyKey: partKey,
    isSaving: isAddingPart,
    trackSave: trackAddPart,
    resetAfterSuccess: resetPartKey,
  } = useIdempotentCreate({
    fingerprint: partFingerprint,
  });
  const selfSubKeyRef = useRef(createIdempotencyKey());
  const client = useApolloClient();
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastAddedPartRef = useRef(null);
  const attachmentSig = (images || [])
    .map((file) => `${file.name}:${file.size}:${file.lastModified}`)
    .join(",");
  const {
    textKey,
    ensureUploaded,
    trackSend,
    isSending,
    resetAfterSuccess,
  } = useIdempotentChatSubmit({
    intentFingerprint: `${newMessage}\0${attachmentSig}`,
    images,
    serverUrl,
    onUploadError: () => addNotification("bad", "Upload Failed"),
  });

  const [addPart, addPartInfo] = useMutation(servicePartMutation, {
    onCompleted: (response) => {
      addNotification("good", "Part added successfully");
      setShowPartsModal(false);
      setPartQuantity("");
      setPartNumber("");
      resetPartKey();
      const part = lastAddedPartRef.current;
      const partId = response?.service_part?.id;
      if (part && mutateFunction && partId) {
        const message = `${part.quantity} Part(s) Added:\n${part.partNumber}`;
        const url = new URL(window.location.href);
        const params = url.searchParams;
        if (params.has("tab")) params.set("tab", "Chat");
        else params.append("tab", "Chat");
        mutateFunction({
          variables: {
            ticket_event_mutation_request: mutationInput({
              properties: [
                { key: "type_id", number: TICKET_EVENT_TYPE.PART },
                { key: "message", string: message },
                { key: "part_id", number: Number(partId) },
                { key: "ticket_id", number: ticket_id },
                { key: "user_id", string: user._id },
                { key: "user_display_name", string: user.name ?? user.username },
                { key: "url", string: url.toString() },
              ],
              idempotencyKey: `${partKey}-event`,
            }),
          },
        }).then(() => refetchEntity());
      }
      lastAddedPartRef.current = null;
    },
    onError: (err) => {
      addNotification("bad", err?.message || "Failed to add part");
    },
  });

  const people = useUserList();
  const userList = [...new Set(
    people.filter((item) => item.id != user._id && item.username).map((item) => item.username)
  )];

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };


    window.addEventListener("resize", handleResize);

    // Cleanup event listener on component unmount
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  // variables for entity query const
  let variables = {
    ticket_event_list_request: {
      filters: [
        {
          field: "ticket_id",
          operator: "=",
          value: String(ticket_id),
        },
      ],
      sorting: {
        field: "id",
        direction: "desc",
      },
    },
    ticket_list_request: {
      filters: [
        {
          field: "id",
          operator: "=",
          value: String(ticket_id),
        },
      ],
    },
  };


  // fetches entity and sets up refetch
  const {
    data,
    loading: loadingEntity,
    refetch: refetchEntity,
  } = useQuery(entityQuery, {
    variables,
    fetchPolicy: "network-only",
    onError: (error) => {
      console.error(error);
    },
  });


  const getSubscriptionId = async () => {
    try {
      const response = await client.query({
        query: subscriptionQuery,
        variables: {
          ticket_subscription_list_request: {
            filters: [
              {
                field: "ticket_id",
                operator: "=",
                value: String(ticket_id),
              },
              {
                field: "user_id",
                operator: "=",
                value: String(user._id),
              },
            ]
          },
          ticket_notification_list_request: {
            filters: [
              {
                field: "ticket_id",
                operator: "=",
                value: String(ticket_id),
              },
              {
                field: "sender_id",
                operator: "=",
                value: String(user._id),
              },
            ]
          },
        },
        fetchPolicy: "network-only"
      });
      const ticket_subscriptions = response?.data?.ticket_subscription?.edges ?? [];
      if (ticket_subscriptions.length > 0) {
        setSubscriptionId(ticket_subscriptions[0].id);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }


  const updateReadStatus = async (data) => {
    try {
      const response = await client.mutate({
        mutation: eventCheckMutation,
        variables: {
          ticket_event_check_mutation_request: {
            id: data.id,
            properties: [
              { key: "ticket_id", number: ticket_id },
              { key: "receiver_id", string: user._id },
              { key: "read_status", number: 1 },
            ],
          },
        },
      });
    } catch (err) {
      console.error(err);
    }
  }


  const getEventCheck = async () => {
    try {
      const response = await client.query({
        query: ticketEventCheckQuery,
        variables: {
          ticket_event_check_list_request: {
            filters: [
              {
                field: "ticket_id",
                operator: "=",
                value: String(ticket_id),
              },
              {
                field: "receiver_id",
                operator: "=",
                value: String(user._id),
              },
              {
                field: "read_status",
                operator: "=",
                value: String(0),
              },
            ]
          },
        },
        fetchPolicy: "network-only"
      });
      const result = response?.data?.ticket_event_check?.edges ?? [];
      console.log(result);
      if (result.length > 0) {
        updateReadStatus(result[0]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }
  const events = data?.ticket_event?.edges ?? [];
  const shownEvents = events;
  const ticket = data?.ticket?.edges?.[0];
  const ticketOpen = Number(ticket?.status_id) !== 2;


  // sets up mutation
  const [mutateFunction, mutateInfo] = useMutation(entityMutation);
  const [mutateTicketFunction, mutateProspectInfo] = useMutation(ticketMutation);


  const sendMessage = () =>
    trackSend(async () => {
      const messageKey = textKey();
      const sentMessage = newMessage;
      let promises = Promise.resolve();
      let changes = "";
      const url = new URL(window.location.href);
      const params = url.searchParams;
      if (params.has("tab")) {
        params.set("tab", "Chat");
      } else {
        params.append("tab", "Chat");
      }
      const pageUrl = url.toString();

      if (previewImages && previewImages.length > 0) {
        setIsUploading(true);
        let uploaded = [];
        try {
          uploaded = await ensureUploaded();
        } finally {
          setIsUploading(false);
        }
        if (!uploaded.length) return;

        for (const { key, path } of uploaded) {
          promises = promises.then(() =>
            mutateFunction({
              variables: {
                ticket_event_mutation_request: {
                  idempotency_key: key,
                  properties: [
                    { key: "type_id", number: TICKET_EVENT_TYPE.IMAGE },
                    { key: "message", string: path },
                    { key: "ticket_id", number: ticket_id },
                    { key: "user_id", string: user._id },
                    {
                      key: "user_display_name",
                      string: user.name ?? user.username,
                    },
                    { key: "url", string: pageUrl },
                  ],
                },
              },
            })
          );
        }
      }

      if (newMessage.trim() !== "") {
        promises = promises.then(() =>
          mutateFunction({
            variables: {
                ticket_event_mutation_request: {
                  idempotency_key: messageKey,
                properties: [
                  { key: "type_id", number: TICKET_EVENT_TYPE.MESSAGE },
                  { key: "message", string: newMessage },
                  ...(changes === ""
                    ? []
                    : [{ key: "changes", string: changes }]),
                  { key: "ticket_id", number: ticket_id },
                  { key: "user_id", string: user._id },
                  {
                    key: "user_display_name",
                    string: user.name ?? user.username,
                  },
                  { key: "url", string: pageUrl },
                ],
              },
            },
          })
        );
      }

      return promises
        .then(() => {
          refetchEntity();
          setNewMessage((current) => (current === sentMessage ? "" : current));
          setImages([]);
          setPreviewImages([]);
          if (fileInputRef.current) fileInputRef.current.value = "";
          resetAfterSuccess();
        })
        .catch((error) => {
          addNotification("bad", "Something went wrong.");
          console.error(error);
        });
    });

  const subscribeTicket = async () => {
    try {
      let id = 0;
      const response = await client.query({
        query: subscriptionQuery,
        variables: {
          ticket_subscription_list_request: {
            filters: [
              {
                field: "ticket_id",
                operator: "=",
                value: String(ticket_id),
              },
              {
                field: "user_id",
                operator: "=",
                value: String(user._id),
              },
            ]
          },
          ticket_notification_list_request: {
            filters: [
              {
                field: "ticket_id",
                operator: "=",
                value: String(ticket_id),
              },
              {
                field: "sender_id",
                operator: "=",
                value: String(user._id),
              },
            ]
          },
        },
        fetchPolicy: "network-only"
      });
      const ticket_subscriptions = response?.data?.ticket_subscription?.edges ?? [];
      if (ticket_subscriptions.length > 0) {
        id = ticket_subscriptions[0].id;
      }
      if (subscriptionId != id) {
        if (subscriptionId) {
          addNotification("good", "Unassigned successfully!");
        } else {
          addNotification("good", "Assigned successfully!");
        }
        setSubscriptionId(id);
        return;
      }
      const subscriptions = await client.mutate({
        mutation: ticketSubscription,
        variables: {
          ticket_subscription_mutation_request: mutationInput({
            id: subscriptionId > 0 ? subscriptionId : undefined,
            shouldDelete: subscriptionId > 0,
            properties: [
              { key: "ticket_id", number: ticket_id },
              { key: "user_id", string: user._id },
            ],
            idempotencyKey:
              subscriptionId > 0 ? undefined : selfSubKeyRef.current,
          }),
        },
      });
      if (subscriptionId) {
        setSubscriptionId(0);
        addNotification("good", "Unassigned successfully!");
      } else {
        setSubscriptionId(subscriptions?.data?.ticket_subscription?.id);
        selfSubKeyRef.current = createIdempotencyKey();
        addNotification("good", "Assigned successfully!");
      }
    } catch (err) {
      addNotification("bad", "Something went wrong.");
      console.error(err);
    }
  }


  const refreshTicketEvent = () => {
    refetchEntity();
  }


  const handleChange = (e) => {
    const value = e.target.value;
    setNewMessage(value);


    // Detect "@" and show suggestions
    const match = value.match(/@([^\s@]*)$/);
    if (match) {
      const search = match[1].toLowerCase();
      const filteredUsers = userList.filter((user) =>
        user.toLowerCase().startsWith(search)
      );
      setSuggestions(filteredUsers);
      setShowSuggestions(filteredUsers.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };


  const handleSelectUser = (user) => {
    setNewMessage((prev) => prev.replace(/@[^\s@]*$/, `@${user} `));
    setShowSuggestions(false);
    inputRef.current.focus();
  };


  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setImages(files);
    const previews = files.map(file => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    });

    Promise.all(previews)
      .then(results => setPreviewImages(results))
      .catch(err => console.error('Image preview error', err));
  };


  const removeImage = (index) => {
    setPreviewImages((prev) => prev.filter((_, i) => i !== index));
    setImages((prev) => prev.filter((_, i) => i !== index));
  }


  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  useEffect(() => {
    localStorage.setItem('channel', `ticket-${ticket_id}`);
    const broadcast = new BroadcastChannel("notification-channel");
    broadcast.onmessage = (event) => {
      if (event.data && event.data.data && event.data.data.url) {
        const url = new URL(event.data.data.url);
        const path = url.pathname.startsWith("/") ? url.pathname.substring(1) : url.pathname;
        const pathSegments = path.split("/");

        let channel = "";

        if (pathSegments.length >= 3) {
          if (pathSegments[1] === "edit") {
            if (pathSegments[0] === "ticket") {
              channel = `ticket-${pathSegments[2]}`;
            } else if (pathSegments[0] === "prospect") {
              channel = `prospect-${pathSegments[2]}`;
            }
          }
        }
        if (channel == localStorage.getItem('channel')) {
          refetchEntity();
          getEventCheck();
        }
      }
    };
    getSubscriptionId();
    getEventCheck();
    return () => {
      localStorage.removeItem('channel');
      broadcast.close(); // Clean up when component unmounts
    }
  }, []);


  return loadingEntity ? (
    phone ? (
      <div className="phone-chat-skeleton" aria-busy="true" aria-label="Loading chat">
        <span /><span className="own" /><span />
      </div>
    ) : (
      <LoadingView />
    )
  ) : (
    <EventContainer className="ticket-event-container" $phone={ phone }>
      { phone && (
        <div className="phone-section-title phone-chat-head">
          <span>Chat</span>
          <span className="phone-chat-actions">
            { canAddParts && ticketOpen && (
              <button type="button" className="phone-chat-link" onClick={ () => setShowPartsModal(true) }>+ Parts</button>
            ) }
            { canAssignTechnicians && ticketOpen && (
              <button type="button" className="phone-chat-link" onClick={ subscribeTicket }>
                { subscriptionId ? "Unassign" : "Assign" }
              </button>
            ) }
          </span>
        </div>
      ) }
      <MessageInput className="ticket-event-input-shell" $phone={ phone }>
        { !phone && <div
          className="ticket-event-toolbar"
          style={ {
            display: "flex",
            alignItems: "center",
            marginBottom: "8px",
            justifyContent: "space-between",
          } }
        >
          { isMobile ? (
            <div style={ { display: "block" } }>
              <div style={ { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" } }>
                <div style={ { display: "flex" } }>
                  <h4 style={ { margin: 0, marginRight: "5px" } }>Update ticket</h4>
                  <TbRefresh size={ 19 } title="Refresh Chat" style={ { cursor: "pointer" } } onClick={ refreshTicketEvent } />
                </div>
                <div style={ { display: "flex", alignItems: "center", gap: "10px" } }>
                  { canAddParts && ticketOpen && <Button className="ticket-event-action-btn" onClick={ () => setShowPartsModal(true) }>+ Parts</Button> }
                  { canAssignTechnicians && ticketOpen && <Button className="ticket-event-action-btn" onClick={ subscribeTicket }>{ subscriptionId ? "Unassign" : "Assign" }</Button> }
                </div>
              </div>
            </div>
          ) : (
            <>
              <div style={ { display: "flex" } }>
                <h4 style={ { margin: 0, marginRight: "5px" } }>Update ticket</h4>
                <TbRefresh size={ 19 } title="Refresh Chat" style={ { cursor: "pointer" } } onClick={ refreshTicketEvent } />
              </div>
              <div
                style={ {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "end",
                  gap: "10px",
                } }
              >
                { canAddParts && ticketOpen && <Button className="ticket-event-action-btn" onClick={ () => setShowPartsModal(true) }>+ Parts</Button> }
                { canAssignTechnicians && ticketOpen && <Button className="ticket-event-action-btn" onClick={ subscribeTicket }>{ subscriptionId ? "Unassign" : "Assign" }</Button> }
              </div>
            </>
          ) }
        </div>
        }
        <div className="ticket-event-composer" style={ { display: "flex", alignItems: "center", position: "relative", flexWrap: phone ? "nowrap" : undefined, gap: phone ? 8 : undefined } }>
          <Input
            className="ticket-event-input"
            $phone={ phone }
            type="text"
            placeholder="Type a message..."
            value={ newMessage }
            onChange={ handleChange }
            onKeyPress={ (e) => e.key === "Enter" && sendMessage() }
            ref={ inputRef }
          />
          { showSuggestions && (
            <ul
              className="ticket-event-mention-list"
              style={ {
                position: "absolute",
                listStyle: "none",
                padding: "5px",
                margin: 0,
                top: "100%",
                bottom: "auto",
                left: 0,
                width: phone ? "100%" : "200px",
                maxHeight: phone ? "200px" : undefined,
                overflow: phone ? "auto" : undefined,
                background: "var(--card-bg)",
                zIndex: 5,
              } }
            >
              { suggestions.map((user) => (
                <li
                  key={ user }
                  style={ { padding: "5px", cursor: "pointer" } }
                  onClick={ () => handleSelectUser(user) }
                >
                  @{ user }
                </li>
              )) }
            </ul>
          ) }
          <input
            id="image-upload"
            type="file"
            accept="image/*"
            multiple
            onChange={ handleImageChange }
            disabled={ isUploading }
            ref={ fileInputRef }
            style={ { display: 'none' } }
          />
          { phone ? (
            <button type="button" className="phone-chat-attach" aria-label="Add photo" onClick={ triggerFileInput } disabled={ isUploading }>
              <FaPlus size={ 16 } />
            </button>
          ) : (
            <FaPlus
              className="ticket-event-attach-btn"
              style={ { marginLeft: '10px', cursor: 'pointer' } }
              size={ 20 }
              onClick={ triggerFileInput }
              disabled={ isUploading }
            />
          ) }
          <ChatSendButton
            className="ticket-event-submit-btn"
            onClick={ sendMessage }
            sending={ isSending }
            disabled={ newMessage.trim() === "" && previewImages.length === 0 }
            phone={ phone }
          />
        </div>
        <div className={ phone ? "phone-chat-previews" : undefined }>
          { previewImages.length > 0 && (
            <div style={ { display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' } }>
              { previewImages.map((img, idx) => (
                <div
                  key={ idx }
                  style={ {
                    position: 'relative',
                    width: '100px',
                    height: '100px'
                  } }
                >
                  <img
                    src={ img }
                    alt={ `preview-${idx}` }
                    style={ {
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '6px'
                    } }
                  />
                  <div
                    onClick={ () => removeImage(idx) }
                    style={ {
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      background: 'rgba(0,0,0,0.6)',
                      color: 'white',
                      borderRadius: '50%',
                      width: '20px',
                      height: '20px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                      cursor: 'pointer'
                    } }
                  >
                    ×
                  </div>
                </div>
              )) }
            </div>
          ) }
        </div>
      </MessageInput>
      <div
        className={ phone ? "phone-chat-thread" : undefined }
        style={ phone ? {
          display: "flex",
          flexDirection: "column",
        } : {
          display: "flex",
          flexDirection: "column",
          maxHeight: 440,
          overflowY: "scroll",
        } }
      >
        { shownEvents.map((event) => (
          <React.Fragment key={ event.id }>
            { isImageTypeId(event.type_id) && <LoadImage data={ event } own={ event.user_id === user._id } phone={ phone } /> }
            { (isPartTypeId(event.type_id) || event.part_id) && event.service_part && (
              <PartBubble className="ticket-event-part-bubble" $phone={ phone }>
                <div className="part-title">Part added</div>
                <div>{ event.service_part.quantity }× { event.service_part.part_no }</div>
                { event.service_part.description && <div>{ event.service_part.description }</div> }
                { event.created_at && (
                  <div className="time">
                    { event.user_display_name ? `${event.user_display_name} - ` : "" }
                    { DateTime.fromISO(event.created_at, { zone: 'utc' }).toLocal().toFormat("MMM d, h:mm a") }
                  </div>
                ) }
              </PartBubble>
            ) }
            { !isImageTypeId(event.type_id) && !isPartTypeId(event.type_id) && !event.part_id && <MessageBubble className={ `ticket-event-message-bubble ${event.user_id === user._id ? "own" : "other"}` } own={ event.user_id === user._id } $phone={ phone }>
              <div className="message">
                <MessageWithLinks message={ phone ? withDisplayMentions(event.message, people) : event.message } />
              </div>
              { event.changes && <div className="changes">{ event.changes }</div> }
              { event.created_at && (
                <div className="time">
                  { event.user_display_name
                    ? `${event.user_display_name} - `
                    : "" }
                  { DateTime.fromISO(event.created_at, { zone: 'utc' }).toLocal().toFormat("MMM d, h:mm a") }
                </div>
              ) }
            </MessageBubble> }
          </React.Fragment>
        )) }
      </div>

      { showPartsModal && (
        <div className="dialog-backdrop">
          <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="parts-modal-header">
            <h3 id="parts-modal-header">Part Added</h3>
            <div className="edit-property" style={ { marginBottom: "12px" } }>
              <label>Quantity:</label>
              <input
                type="number"
                min={ 1 }
                step={ 1 }
                value={ partQuantity }
                onChange={ (e) => {
                  const v = e.target.value;
                  const sanitized = v === "" ? "" : v.replace(/\D/g, "");
                  setPartQuantity(sanitized);
                } }
                placeholder="Enter quantity"
                style={ { width: "100%", padding: "8px" } }
              />
            </div>
            <div className="edit-property" style={ { marginBottom: "16px" } }>
              <label>Part Number:</label>
              <input
                type="text"
                value={ partNumber }
                onChange={ (e) => setPartNumber(e.target.value) }
                placeholder="Enter part number"
                style={ { width: "100%", padding: "8px" } }
              />
            </div>
            <div className="dialog-actions">
              <button onClick={ () => { setShowPartsModal(false); setPartQuantity(""); setPartNumber(""); } }>Cancel</button>
              <SaveButton
                saving={ isAddingPart || addPartInfo.loading }
                disabled={ !partQuantity.trim() || !partNumber.trim() }
                onClick={ () => {
                  const qty = parseInt(partQuantity.trim(), 10);
                  if (Number.isNaN(qty) || qty < 1) {
                    addNotification("bad", "Please enter a valid quantity (positive number).");
                    return;
                  }
                  const properties = [
                          { key: "quantity", number: qty },
                          { key: "part_no", string: partNumber.trim() },
                          { key: "ticket_id", number: Number(ticket_id) },
                        ];
                  if (crane_id != null) properties.push({ key: "crane_id", number: Number(crane_id) });
                  if (user?._id != null) properties.push({ key: "user_id", number: Number(user._id) });
                  lastAddedPartRef.current = { quantity: qty, partNumber: partNumber.trim() };
                  trackAddPart(() => addPart({
                    variables: {
                      service_part_mutation_request: mutationInput({
                        properties,
                        idempotencyKey: partKey,
                      }),
                    },
                  }));
                } }
              >
                Add
              </SaveButton>
            </div>
          </div>
        </div>
      ) }
    </EventContainer>
  );
};


export default TicketEvent;